import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';
import { cn } from '../../common/functions/cn';
import { buttonV2Variants } from '../button-v2/button-v2';
import { Separator } from '../separator/separator';

const buttonGroupVariants = cva(
  [
    // min-w-0 clears the UA's min-inline-size: min-content on fieldset, which preflight leaves alone.
    'flex w-fit min-w-0 items-stretch',
    // Lift the focused child so its ring is not clipped by the neighbour overlapping it.
    '*:focus-visible:relative *:focus-visible:z-10',
    // A group reads as one control, so a child must not dip away from the seam when it is pressed.
    '[&>[data-slot]:active:not([data-slot=button-group-separator])]:translate-y-0',
    '*:data-slot:bg-clip-border',
    'has-[>[data-slot=button-group]]:gap-2',
  ],
  {
    variants: {
      orientation: {
        horizontal: [
          '*:data-slot:rounded-e-none',
          '[&>[data-slot]:not(:has(~[data-slot]))]:rounded-e-md!',
          '[&>[data-slot]~[data-slot]]:rounded-s-none [&>[data-slot]~[data-slot]]:border-s-0',
          // The child before a checked one lends it a leading edge by recolouring the border they share, so a
          // selection closes its own outline without a child ever gaining a border to lay out around.
          '[&>[data-slot]:has(+[data-slot]_:checked):not([data-slot=button-group-separator])]:border-e-ring',
          // Each child but the last tints its trailing edge, so the divider is a shade of that
          // button rather than a fixed grey laid over it. The separator draws nothing itself.
          '[&>[data-slot]:has(~[data-slot]):not([data-slot=button-group-separator]):not(:has(:checked)):not(:has(+[data-slot]_:checked))]:border-e-current/12',
        ],
        vertical: [
          'flex-col *:data-slot:rounded-b-none',
          '[&>[data-slot]:not(:has(~[data-slot]))]:rounded-b-md!',
          '[&>[data-slot]~[data-slot]]:rounded-t-none [&>[data-slot]~[data-slot]]:border-t-0',
          '[&>[data-slot]:has(+[data-slot]_:checked):not([data-slot=button-group-separator])]:border-b-ring',
          '[&>[data-slot]:has(~[data-slot]):not([data-slot=button-group-separator]):not(:has(:checked)):not(:has(+[data-slot]_:checked))]:border-b-current/12',
        ],
      },
    },
    defaultVariants: {
      orientation: 'horizontal',
    },
  }
);

type ButtonGroupProps = ComponentProps<'fieldset'> & VariantProps<typeof buttonGroupVariants>;

// name is required: radios only exclude one another when they share one.
type ButtonGroupRadioProps = Omit<ComponentProps<'input'>, 'type'> & { name: string };

const ButtonGroup = ({ className, orientation = 'horizontal', ...props }: ButtonGroupProps) => (
  <fieldset
    data-slot="button-group"
    data-orientation={orientation}
    className={cn(buttonGroupVariants({ orientation, className }))}
    {...props}
  />
);

const ButtonGroupText = ({ className, ...props }: ComponentProps<'div'>) => (
  <div
    data-slot="button-group-text"
    className={cn(
      "flex items-center gap-2 rounded-md border border-current/15 px-2.5 font-medium text-sm [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none",
      className
    )}
    {...props}
  />
);

const ButtonGroupSeparator = ({ className, orientation = 'vertical', ...props }: ComponentProps<typeof Separator>) => (
  <Separator
    data-slot="button-group-separator"
    orientation={orientation}
    className={cn(
      'relative self-stretch bg-transparent',
      orientation === 'vertical' ? 'my-px h-auto w-0' : 'mx-px h-0 w-auto',
      className
    )}
    {...props}
  />
);

/**
 * A radio styled as a group button, for one choice among a few.
 * Give every choice in a group the same name, or each one becomes a group of one.
 * Disable them together with `disabled` on the ButtonGroup, which the fieldset applies to every control inside it.
 *
 * `className` styles the visible label rather than the input, which is only ever visually hidden.
 */
const ButtonGroupRadio = ({ className, children, ...props }: ButtonGroupRadioProps) => (
  <label
    data-slot="button"
    className={cn(
      buttonV2Variants({ variant: 'outline' }),
      'cursor-pointer',
      'transition-[color,background-color,box-shadow]',
      'has-checked:border-ring has-checked:bg-accent has-checked:text-accent-foreground has-checked:hover:bg-accent',
      'has-focus-visible:relative has-focus-visible:z-20',
      'has-focus-visible:border-ring has-focus-visible:ring-2 has-focus-visible:ring-ring/50',
      'has-disabled:pointer-events-none has-disabled:opacity-50',
      // Forced colours discard the checked background, which would leave the selection invisible.
      'forced-colors:has-checked:bg-[Highlight] forced-colors:has-checked:text-[HighlightText]',
      'forced-colors:has-checked:hover:bg-[Highlight]',
      className
    )}
  >
    <input className="sr-only" {...props} type="radio" />
    {children}
  </label>
);

export {
  ButtonGroup,
  type ButtonGroupProps,
  ButtonGroupRadio,
  type ButtonGroupRadioProps,
  ButtonGroupSeparator,
  ButtonGroupText,
  buttonGroupVariants,
};
